import React, { useEffect, useRef } from 'react'

export type ActionMenuItem = {
    key: string
    label: string
    onClick: () => void
    disabled?: boolean
    danger?: boolean
    icon?: string
    logo?: string
}

type Props = {
    open: boolean
    anchorRef: React.RefObject<HTMLElement | null>
    onClose: () => void
    items: ActionMenuItem[]
    placement?:
        | 'bottom-start'
        | 'bottom-end'
        | 'top-start'
        | 'top-end'
        | 'right-start'
        | 'left-start'
    width?: number
    theme: string
}

const ActionMenu: React.FC<Props> = ({
    open,
    anchorRef,
    onClose,
    items,
    placement = 'bottom-start',
    width = 220,
    theme,
}) => {
    const menuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node
            if (
                menuRef.current &&
                !menuRef.current.contains(target) &&
                anchorRef.current &&
                !anchorRef.current.contains(target)
            ) {
                onClose()
            }
        }

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEscape)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleEscape)
        }
    }, [open, onClose, anchorRef])

    if (!open || !anchorRef.current) return null

    const rect = anchorRef.current.getBoundingClientRect()

    const style: React.CSSProperties = {
        position: 'fixed',
        zIndex: 1000,
        width,
    }

    switch (placement) {
        case 'bottom-start':
            style.top = rect.bottom + 6
            style.left = rect.left
            break
        case 'bottom-end':
            style.top = rect.bottom + 6
            style.left = rect.right - width
            break
        case 'top-start':
            style.top = rect.top - 6
            style.left = rect.left
            style.transform = 'translateY(-100%)'
            break
        case 'top-end':
            style.top = rect.top - 6
            style.left = rect.right - width
            style.transform = 'translateY(-100%)'
            break
        case 'right-start':
            style.top = rect.top
            style.left = rect.right + 6
            break
        case 'left-start':
            style.top = rect.top
            style.left = rect.left - width - 6
            break
    }

    return (
        <div
            ref={menuRef}
            style={style}
            className="flex flex-col w-[200px] rounded-lg p-1 text-[12.8px] items-start justify-start gap-1 border border-[#b8b8b8] dark:border-[#696769] bg-[#d9dce0] dark:bg-[#333033] shadow-lg overflow-hidden"
        >
            <div className="flex flex-col w-full">
                {items.map((item) => (
                    <button
                        key={item.key}
                        type="button"
                        disabled={item.disabled}
                        className={[
                            'flex flex-row gap-2 p-1 pl-2 w-full items-center justify-start rounded-lg',
                            'text-[#181818] dark:text-[#FFFFFF]',
                            item.disabled
                                ? 'opacity-50 cursor-not-allowed'
                                : 'hover:bg-[#e9e9e9] dark:hover:bg-[#656365]',
                            item.danger
                                ? 'text-[#a02845] dark:text-[#ff9cb3]'
                                : '',
                        ].join(' ')}
                        onClick={() => {
                            if (item.disabled) return
                            item.onClick()
                            onClose()
                        }}
                    >
                        {item.icon ? (
                            item.icon && (
                                <svg
                                    className="w-4 h-4"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    id={item.key}
                                >
                                    <path
                                        fill={theme == 'dark' ? '#fff' : '#000'}
                                        d={item.icon}
                                    ></path>
                                </svg>
                            )
                        ) : item.logo ? (
                            <img
                                src={`${process.env.REACT_APP_API_URL}${item.logo}`}
                                alt="Логотип организации"
                                className="flex w-10 h-10 object-cover"
                            />
                        ) : (
                            ''
                        )}

                        {item.label}
                    </button>
                ))}
            </div>
        </div>
    )
}

export default ActionMenu
